'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';
import { createClient } from '@/lib/supabase/client';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

export default function AdminLoginForm() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const supabase = createClient();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      const { data: authData, error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      if (error) {
        setError('root', { message: 'Invalid credentials. Please check your email and password.' });
        setIsLoading(false);
        return;
      }

      // Verify the signed-in user has admin role
      const role = authData.user?.user_metadata?.role || authData.user?.app_metadata?.role;
      if (role !== 'admin') {
        await supabase.auth.signOut();
        setError('root', { message: 'Access denied. This portal is for admins only.' });
        setIsLoading(false);
        return;
      }

      toast.success('Welcome back, Admin!', {
        description: 'Redirecting to admin panel…',
      });
      setTimeout(() => router.push('/admin-panel'), 800);
    } catch {
      setError('root', { message: 'An unexpected error occurred. Please try again.' });
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md">
      {/* Logo */}
      <div className="flex flex-col items-center mb-8">
        <div className="flex items-center gap-2.5 mb-3">
          <AppLogo size={44} />
          <span className="font-bold text-2xl text-foreground tracking-tight">
            MathSheets
          </span>
        </div>
        <p className="text-sm text-muted-foreground">Admin Portal</p>
      </div>

      {/* Card */}
      <div className="card-base p-8">
        <h1 className="text-xl font-bold text-foreground mb-1">Sign in</h1>
        <p className="text-sm text-muted-foreground mb-6">
          Access the worksheet management dashboard.
        </p>

        {/* Root error */}
        {errors.root && (
          <div className="flex items-start gap-2.5 p-3 rounded-lg bg-red-50 border border-red-200 mb-5">
            <ShieldCheck size={15} className="text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-600">{errors.root.message}</p>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {/* Email */}
          <div className="mb-4">
            <label htmlFor="email" className="label-text">
              Email address
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              className={`input-field ${errors.email ? 'border-red-400 focus:ring-red-400' : ''}`}
              placeholder="admin@arinnahouse.com"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Enter a valid email address',
                },
              })}
            />
            {errors.email && (
              <p className="error-text">{errors.email.message}</p>
            )}
          </div>

          {/* Password */}
          <div className="mb-4">
            <label htmlFor="password" className="label-text">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                className={`input-field pr-10 ${errors.password ? 'border-red-400 focus:ring-red-400' : ''}`}
                placeholder="Enter your password"
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 6,
                    message: 'Password must be at least 6 characters',
                  },
                })}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-150"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.password && (
              <p className="error-text">{errors.password.message}</p>
            )}
          </div>

          {/* Remember me */}
          <div className="flex items-center gap-2 mb-6">
            <input
              id="rememberMe"
              type="checkbox"
              className="w-4 h-4 rounded border-input accent-primary cursor-pointer"
              {...register('rememberMe')}
            />
            <label
              htmlFor="rememberMe"
              className="text-sm text-muted-foreground cursor-pointer select-none"
            >
              Keep me signed in
            </label>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full btn-primary justify-center py-2.5 text-sm scale-press"
            style={{ minHeight: '40px' }}
          >
            {isLoading ? (
              <>
                <Loader2 size={15} className="animate-spin" />
                Signing in…
              </>
            ) : (
              'Sign in to Admin Panel'
            )}
          </button>
        </form>
      </div>

      <p className="text-center text-xs text-muted-foreground mt-5">
        Public users don&apos;t need an account.{' '}
        <a href="/" className="text-primary font-semibold hover:underline">
          Browse worksheets →
        </a>
      </p>
    </div>
  );
}